/* CURAM PostHog storefront v2 — 2 October 2026. Consent-gated analytics and masked replay. */
(() => {
  'use strict';
  if (window.__curamPostHog || window.Shopify?.designMode) return;
  window.__curamPostHog = true;
  const TOKEN = 'phc_sUC7XiPm9ncS6mAdmaQMqR5EujFCWMhQXU5g5YbuVLc8';
  const ID_COOKIE = 'curam_ph_id';
  let started = false;
  let enabled = false;
  let ready = false;
  const permittedPage = !/\/(account|checkouts?|orders|search)(\/|$)/i.test(location.pathname);
  if (!permittedPage) return;

  function consent() {
    try {
      const c = window.getCkyConsent?.();
      return c?.isUserActionCompleted === true && c?.categories?.analytics === true;
    } catch (_) { return false; }
  }
  function cookieId() {
    const existing = document.cookie.split('; ').find(s => s.startsWith(ID_COOKIE + '='))?.split('=')[1];
    if (existing && /^[a-f0-9-]{36}$/i.test(existing)) return existing;
    const id = crypto.randomUUID();
    document.cookie = ID_COOKIE + '=' + id + '; Path=/; Domain=bycuram.com; Max-Age=31536000; SameSite=Lax; Secure';
    return id;
  }
  function cleanUrl(value) {
    try {
      const url = new URL(value, location.origin);
      return url.origin + url.pathname;
    } catch (_) { return ''; }
  }
  function replayAttribute(name, value) {
    // Retain public layout, but never input values, labels or arbitrary data attributes.
    if (/^(class|id|style|type|width|height|viewBox|d|fill|stroke|stroke-width|xmlns|role|preserveAspectRatio|rel|media|_cssText)$/i.test(name)) return value;
    if (/^(href|src|poster)$/i.test(name)) {
      try {
        const url = new URL(value, location.origin);
        if (!/^https?:$/.test(url.protocol) || /\/(account|checkouts?|orders|search)(\/|$)/i.test(url.pathname)) return '';
        return url.origin + url.pathname;
      } catch (_) { return ''; }
    }
    return '';
  }
  function clean(value) {
    if (typeof value === 'string' && /^https?:\/\//.test(value)) return cleanUrl(value);
    if (Array.isArray(value)) return value.map(clean);
    if (value && typeof value === 'object') {
      const result = {};
      Object.keys(value).forEach(k => {
        if (/email|phone|address|password|search|^\x24set|^\x24initial|^utm_|^gclid$|^fbclid$/i.test(k)) return;
        result[/^https?:\/\//.test(k) ? cleanUrl(k) : k] = clean(value[k]);
      });
      return result;
    }
    return value;
  }
  function start() {
    if (started || !consent()) return;
    if (window.posthog) { console.warn('[CURAM] Existing PostHog detected; duplicate setup skipped.'); return; }
    started = true;
    const distinctId = cookieId();
    const ph = window.posthog = [];
    ph._i = [];
    ph.__SV = 1;
    ph.people = [];
    ph.toString = () => 'posthog (stub)';
    ph.people.toString = () => 'posthog.people (stub)';
    ph.init = function (key, options) { ph._i.push([key, options, 'posthog']); };
    ['capture', 'opt_in_capturing', 'opt_out_capturing', 'reset', 'set_config', 'startSessionRecording', 'stopSessionRecording'].forEach(method => {
      ph[method] = function () { ph.push([method].concat(Array.from(arguments))); };
    });
    ph.init(TOKEN, {
      api_host: 'https://eu.i.posthog.com', ui_host: 'https://eu.posthog.com',
      defaults: '2026-05-30', bootstrap: { distinctID: distinctId, isIdentifiedID: false },
      person_profiles: 'never', persistence: 'localStorage', ip: false,
      capture_pageview: false, capture_pageleave: true,
      autocapture: { dom_event_allowlist: ['click'] },
      mask_all_text: true, mask_all_element_attributes: true,
      capture_heatmaps: true, capture_dead_clicks: false,
      capture_exceptions: false, capture_performance: false,
      disable_session_recording: true, disable_surveys: true,
      enable_recording_console_log: false, disable_capture_url_hashes: true,
      session_recording: {
        maskAllInputs: true, maskTextSelector: '*',
        blockSelector: 'script, iframe, video, audio, canvas, input[type="hidden"], input[type="file"], [contenteditable]',
        maskAttributeFn: replayAttribute,
        recordCrossOriginIframes: false, recordHeaders: false, recordBody: false,
        captureJsonLd: false, captureCanvas: { recordCanvas: false },
        slimDOMOptions: 'all',
        maskCapturedNetworkRequestFn: request => {
          // This also redacts the URL shown in the replay player.
          if (request.name) request.name = cleanUrl(request.name);
          return request;
        }
      },
      disable_web_experiments: true, advanced_disable_feature_flags: true,
      opt_out_capturing_by_default: true, opt_out_persistence_by_default: true,
      before_send: event => {
        if (!enabled || !consent()) return null;
        // Replay is already masked by the recorder. Do not recursively rewrite its structure.
        if (event.event === '$snapshot') return event;
        if (!['$pageview', '$pageleave', '$autocapture', '$$heatmap'].includes(event.event)) return null;
        event.properties = clean(event.properties || {});
        event.properties.$current_url = cleanUrl(location.href);
        event.properties.$process_person_profile = false;
        event.properties.integration_version = 'curam-2026-10-02-v2';
        event.properties.environment = window.Shopify?.theme?.role === 'main' ? 'production' : 'preview';
        return event;
      },
      loaded: instance => {
        ready = true;
        if (consent()) {
          enabled = true;
          instance.opt_in_capturing({ captureEventName: false });
          instance.capture('$pageview');
          instance.startSessionRecording();
        } else instance.opt_out_capturing();
      }
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://eu-assets.i.posthog.com/static/array.js';
    document.head.appendChild(script);
  }
  function sync() {
    // CookieYes initialises asynchronously on every page. Unknown is not withdrawal.
    // Keep the existing consented journey ID while waiting, but load/capture nothing.
    if (window.getCkyConsent?.()?.isUserActionCompleted !== true) return;
    const allowed = consent();
    if (!allowed) {
      enabled = false;
      if (started) {
        window.posthog.stopSessionRecording();
        window.posthog.opt_out_capturing();
      }
      document.cookie = ID_COOKIE + '=; Path=/; Domain=bycuram.com; Max-Age=0; SameSite=Lax; Secure';
      return;
    }
    if (!started) { start(); return; }
    if (ready && !enabled) {
      enabled = true;
      // Begin a new anonymous journey after withdrawal, with no link to the previous ID.
      window.posthog.reset();
      const id = window.posthog.get_distinct_id();
      document.cookie = ID_COOKIE + '=' + id + '; Path=/; Domain=bycuram.com; Max-Age=31536000; SameSite=Lax; Secure';
      window.posthog.opt_in_capturing({ captureEventName: false });
      window.posthog.capture('$pageview');
      window.posthog.startSessionRecording();
    }
  }
  document.addEventListener('cookieyes_banner_load', sync);
  document.addEventListener('cookieyes_consent_update', sync);
  sync();
})();
